import axios from "axios";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { alertInfoTypes } from "shared-components/src/components/feedback/alertInfoTypes.enum";
import { showAlertInfo } from "shared-components/src/components/feedback/store/alertInfo.actions";
import {
  hideProgress,
  showProgress,
} from "shared-components/src/components/feedback/store/circularProgress.actions";
import { API_COMMAND_SENSOR, API_SENSORS } from "../../config/api";
import {
  ADD_COMMAND_REQUEST,
  ADD_SENSOR,
  DELETE_SENSOR,
  FETCH_COMMAND_REQUEST,
  UPDATE_SENSOR,
  addCommandFailure,
  addCommandSuccess,
  addSensorFailure,
  addSensorSuccess,
  deleteSensorFailure,
  deleteSensorSuccess,
  fetchCommandStatusFailure,
  fetchCommandStatusRequest,
  fetchCommandStatusSuccess,
  updateSensorFailure,
  updateSensorSuccess,
} from "../../store/actions/sensors.actions";
import { fetchFieldsDataRequest } from "../../store/actions/fields.actions";
import { fetchOveviewData } from "../../store/actions/oveview.actions";

const addSensor = async (token, sensor) => {
  const fieldsUrl = `${API_SENSORS}`;
  const data = sensor;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  const response = await axios.post(fieldsUrl, { data }, { headers });
  return response.data;
};

function* handleAddSensor(action) {
  try {
    yield put(showProgress());
    const token = yield select((state) => state.auth.token);
    const farmId = yield select((state) => state.farm.data.id);
    const fieldId = yield select((state) => state.field.data);
    let sensor = action?.payload;
    sensor = { ...sensor, field: fieldId };
    const response = yield call(addSensor, token, sensor);
    console.log(response.data);
    yield put(addSensorSuccess(response));
    yield put(fetchFieldsDataRequest(farmId));
    yield put(fetchOveviewData());
    yield put(hideProgress());
  } catch (error) {
    let errorMessage = error?.response?.data?.error?.message ?? error.message;

    const errors = error?.response?.data?.error?.details?.errors;
    if (errors) {
      const euiError = errors.find(
        (err) =>
          err?.path?.[0] === "eui" &&
          err.message === "This attribute must be unique"
      );

      if (euiError) {
        errorMessage =
          "The EUI value already exists. Please enter a unique EUI.";
      }
    }
    yield put(
      showAlertInfo({
        type: alertInfoTypes.ERROR,
        message: errorMessage,
      })
    );
    yield put(addSensorFailure(errorMessage));
    yield put(hideProgress());
  }
}

export function* watchAddSensor() {
  yield takeLatest(ADD_SENSOR, handleAddSensor);
}

const deleteSensor = async (token, sensor) => {
  const fieldsUrl = `${API_SENSORS}/${sensor?.id}`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  const response = await axios.delete(fieldsUrl, { headers });
  return response.data;
};

function* handleDeleteSensor(action) {
  try {
    yield put(showProgress());
    const token = yield select((state) => state.auth.token);
    const farmId = yield select((state) => state.farm.data.id);
    const sensor = action?.payload;
    const response = yield call(deleteSensor, token, sensor);
    yield put(deleteSensorSuccess(response));
    yield put(fetchFieldsDataRequest(farmId));
    yield put(fetchOveviewData());
    yield put(hideProgress());
  } catch (error) {
    yield put(
      showAlertInfo({
        type: alertInfoTypes.ERROR,
        message: error?.response?.data?.error?.message ?? error.message,
      })
    );
    yield put(deleteSensorFailure(error.message));
    yield put(hideProgress());
  }
}

export function* watchDeleteSensor() {
  yield takeLatest(DELETE_SENSOR, handleDeleteSensor);
}

const updateSensor = async (token, sensor) => {
  const fieldsUrl = `${API_SENSORS}/${sensor?.id}`;
  const data = sensor;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const response = await axios.put(fieldsUrl, { data }, { headers });
  return response.data;
};

function* handleUpdateSensor(action) {
  try {
    yield put(showProgress());
    const token = yield select((state) => state.auth.token);
    const farmId = yield select((state) => state.farm.data.id);
    let sensor = action?.payload;
    const response = yield call(updateSensor, token, sensor);
    yield put(updateSensorSuccess(response));
    yield put(fetchFieldsDataRequest(farmId));
    yield put(fetchOveviewData());
    yield put(hideProgress());
  } catch (error) {
    yield put(
      showAlertInfo({
        type: alertInfoTypes.ERROR,
        message: error?.response?.data?.error?.message ?? error.message,
      })
    );
    yield put(updateSensorFailure(error.message));
    yield put(hideProgress());
  }
}

export function* watchUpdateSensor() {
  yield takeLatest(UPDATE_SENSOR, handleUpdateSensor);
}

const addCommandSensor = async (token, payload) => {
  const url = `${API_COMMAND_SENSOR}`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  const body = {
    data: {
      unit: payload?.unit,
      sensor: payload?.sensor,
      field: payload?.field,
      actionType: payload?.actionType,
      quantity: payload?.quantity,
    },
  };
  const response = await axios.post(url, body, { headers });
  return response.data;
};

function* handleAddCommandSensor(action) {
  try {
    yield put(showProgress());
    const token = yield select((state) => state.auth.token);
    const response = yield call(addCommandSensor, token, action.payload);
    yield put(addCommandSuccess(response));
    const queryParams = {
      fieldId: action.payload.field,
      sensorId: action.payload.sensor,
    };
    yield put(fetchCommandStatusRequest(queryParams));
    yield put(hideProgress());
  } catch (error) {
    yield put(
      showAlertInfo({
        type: alertInfoTypes.ERROR,
        message: error?.response?.data?.error?.message ?? error.message,
      })
    );
    yield put(addCommandFailure(error.message));
    yield put(hideProgress());
  }
}

export function* watchAddCommandSensor() {
  yield takeLatest(ADD_COMMAND_REQUEST, handleAddCommandSensor);
}

const fetchCommandStatus = async (token, payload) => {
  const url = `${API_COMMAND_SENSOR}`;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  const queryParams = {
    "filters[field]": payload?.fieldId,
    "filters[sensor]": payload?.sensorId,
    "sort[0]": "updatedAt:desc",
  };
  const response = await axios.get(url, { params: queryParams, headers });
  return response.data;
};

function* handleFetchCommandStatus(action) {
  try {
    yield put(showProgress());
    const token = yield select((state) => state.auth.token);
    const payload = action?.payload;
    const response = yield call(fetchCommandStatus, token, payload);
    yield put(fetchCommandStatusSuccess(response));
    yield put(hideProgress());
  } catch (error) {
    yield put(
      showAlertInfo({
        type: alertInfoTypes.ERROR,
        message: error?.response?.data?.error?.message ?? error.message,
      })
    );
    yield put(fetchCommandStatusFailure(error.message));
    yield put(hideProgress());
  }
}

export function* watchFetchCommandSensor() {
  yield takeLatest(FETCH_COMMAND_REQUEST, handleFetchCommandStatus);
}
